import { FC, useCallback, useEffect, useState } from 'react';

import { UserAuthModel, useAuth, useOrder, useOtpTimer } from '@/logic/state';
import { useCheckClientCity, useUpdatePath } from '@/logic/hooks';
import { useSendOtp, useVerifyOtp } from '@/logic/services/otp';

import { AnyObject } from 'immer/dist/internal';
import { PATHS } from '@/constants/routes';
import { UserModel } from '@/features/login-and-signup/types/user-model';
import axios from 'axios';
import shallow from 'zustand/shallow';
import { useDialog } from '@/logic/state/dialog';
import { useRouter } from 'next/router';
import { useSnack } from '@/logic/state/snackbar';
import { EventTypes, LoginSuccessModel } from '@/logic/types/data-layer-models';
import { triggerGAEvent } from '@/logic/shared/data-layer-events';
import {
  VerificationCodeForm,
  VerificationCodeFormModel,
} from '../shared/verification-code-form';
import { AUTH_FLOW } from '@/constants/auth';
import ImageBoxFuture from '../shared/image-box-future';
import { Button, Typography, useTheme } from '@mui/material';
import { pxToRem } from '@/theme/typography';
import { FormattedMessage } from 'react-intl';

const emailIcon = '/icons/shared/emailIcon.svg';

export const OtpForm: FC = () => {
  const { locale, pathname, asPath } = useRouter();
  const [showDelayedButton, setShowDelayedButton] = useState<boolean>(false);
  const theme = useTheme();
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [dialog, setDialog] = useDialog(s => [s.dialog, s.setDialog], shallow);
  const setSnack = useSnack(s => s.setSnack, shallow);
  const setUser = useAuth(s => s.setUser, shallow);
  const [setOrder, setGuestBookingInfo, order] = useOrder(
    s => [s.setOrder, s.setGuestBookingInfo, s.order],
    shallow
  );
  const setOtpTimer = useOtpTimer(s => s.setOtpTimer, shallow);
  const { checkUserCity } = useCheckClientCity();
  const { updateLoginPath } = useUpdatePath();

  const reSendOtp = useSendOtp();
  const verifyOtp = useVerifyOtp();

  const handleFormSubmit = (data: VerificationCodeFormModel) => {
    setError(false);
    setErrorMessage('');
    verifyOtp.safeMutateAsync(
      {
        data: {
          ...data,
          phoneNumber: dialog?.phoneNumberFormPayload?.phoneNumber,
        },
      },
      {
        onSuccess: (response: AnyObject) => {
          if (response.next !== AUTH_FLOW.OLD) {
            const { language: _, ...restUserData } = response.user;
            setUser({
              ...restUserData,
              language: locale,
              token: response.token,
              refreshToken: response.refreshToken,
            });
            updateLoginPath();
            setOrder(prevState =>
              prevState
                ? {
                    ...prevState,
                    isGuestBookingEnabled:
                      pathname === PATHS.BOOKINGS_ID_YOUR_TICKET
                        ? prevState?.isGuestBookingEnabled
                        : false,
                  }
                : null
            );
            setGuestBookingInfo(null);
          }
          successFlow(response.next, response.token, response.user, response?.email);
          const data: LoginSuccessModel = {
            event: EventTypes.LOGIN_SUCCESS,
            page_title: document.title,
            page_url: document.URL,
            page_section: asPath,
          };
          triggerGAEvent(data);
        },
        onError: error => {
          if (axios.isAxiosError(error)) {
            setError(true);
            setErrorMessage(error?.response?.data?.message);
          }
        },
      }
    );
  };

  const successFlow = useCallback(
    (next: string, token: string, user: UserAuthModel | UserModel, email: string) => {
      if (next === AUTH_FLOW.HOME) {
        checkUserCity(user);
      }
      if (next === AUTH_FLOW.COMPLETE_PROFILE) {
        setDialog({});
        setTimeout(() => {
          setDialog({
            wrapper: true,
            isCompleteProfileForm: true,
            completeProfileFormPayload: {
              hideMaybeLaterButton: true,
            },
          });
        }, 500);
      }
      if (next === AUTH_FLOW.OLD) {
        setDialog({});
        setTimeout(() => {
          setDialog({
            wrapper: true,
            isOldUserForm: true,
            oldUserFormPayload: {
              token,
              email: email,
              phoneNumber: dialog.phoneNumberFormPayload.phoneNumber,
            },
          });
        }, 500);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkUserCity, setDialog]
  );

  const handleCodeResend = useCallback(() => {
    setOtpTimer(prevState => ({
      ...prevState,
      timerVal: 0,
    }));
    reSendOtp.safeMutateAsync(
      {
        data: {
          phoneNumber: dialog?.phoneNumberFormPayload?.phoneNumber,
        },
      },
      {
        onSuccess: res => {
          setOtpTimer(prevState => ({
            ...prevState,
            timerVal: res?.retryAfter,
          }));
          setSnack({
            isOpen: true,
            message: 'otp.resent.successfully',
            icon: (
              <ImageBoxFuture
                img={emailIcon}
                objectFit='contain'
                sx={{
                  width: '16px',
                  height: '16px',
                }}
              />
            ),
            type: 'info',
          });
        },
        onError: error => {
          if (axios.isAxiosError(error)) {
            setOtpTimer({
              timerVal: 0,
              isRunning: true,
            });
          }
        },
      }
    );
  }, [dialog?.phoneNumberFormPayload?.phoneNumber, reSendOtp, setOtpTimer, setSnack]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowDelayedButton(true);
    }, 5000);

    // Cleanup function to clear the timeout if the component unmounts
    return () => clearTimeout(timer);
  }, []);

  const continueAsGuest = (): void => {
    setDialog({});
    setGuestBookingInfo(prevState => ({
      ...prevState,
      isPhoneNumberVisibleOnly: false,
    }));
    setOrder(prevState => ({
      ...prevState,
      isGuestBookingEnabled: true,
    }));
  };

  return (
    <VerificationCodeForm
      subtitle='verify.phone.number'
      description='global.description.code.phone_number'
      value={dialog?.phoneNumberFormPayload?.phoneNumber}
      isVerifyCodeLoading={verifyOtp.isLoading}
      error={error}
      errorMessage={errorMessage}
      isSendOtpLoading={reSendOtp?.isLoading}
      handleFormClose={() => setDialog({})}
      handleFormSubmit={handleFormSubmit}
      handleCodeResend={handleCodeResend}
      actionComponent={
        <>
          {showDelayedButton && (
            <Button variant='text' onClick={continueAsGuest}>
              <Typography
                sx={{
                  fontWeight: '700',
                  fontSize: pxToRem(16),
                  color: theme.palette.primary.main,
                }}
              >
                <FormattedMessage id='global.continue.as.guest' />
              </Typography>
            </Button>
          )}
        </>
      }
    />
  );
};
